/**
 * Manifest Registry
 *
 * Central registry for built-in operation manifests.
 * Maps operation types to their manifest definitions.
 *
 * Note: Some manifests from LogicAppsUX have been adapted to work with our type system.
 * The manifest format uses more permissive types to accommodate various property shapes.
 */

import type { OperationManifest } from '@/types/operationManifest';

// Control Flow Operations (use default exports)
import conditionManifest from './condition';
import foreachManifest from './foreach';
import switchManifest from './switch';
import scopeManifest from './scope';
import untilManifest from './until';
import terminateManifest from './terminate';

// Variable Operations
import {
  initializeManifest,
  setManifest,
  incrementManifest,
  decrementManifest,
  appendArrayManifest,
  appendStringManifest,
} from './variables';

// HTTP Operations
import {
  httpManifest,
  httpTriggerManifest,
  httpWithSwaggerManifest,
  httpWithSwaggerTriggerManifest,
  httpWebhookManifest,
  httpWebhookTriggerManifest,
} from './http';

// Data Operations (use default exports)
import composeManifest from './compose';
import parseJsonManifest from './parsejson';
import joinManifest from './join';
import selectManifest from './select';
import queryManifest from './query';
import csvTableManifest from './csvtable';
import htmlTableManifest from './htmltable';

// Date/Time Operations
import {
  currentTimeManifest,
  addToTimeManifest,
  subtractFromTimeManifest,
  getFutureTimeManifest,
  getPastTimeManifest,
  convertTimezoneManifest,
} from './datetime';

// Request/Response Operations
import requestManifest from './request';
import responseManifest from './response';

// Schedule Triggers & Delay Operations
import {
  recurrenceManifest,
  // slidingWindowManifest removed - consumption-only, not supported in Otto/Edge
  delayManifest,
  delayUntilManifest,
} from './schedule';

// Inline Code Operations
import {
  inlineJavaScriptManifest,
  inlinePythonManifest,
} from './inlinecode';

// Agent Operations (use default exports)
import agentLoopManifest from './agentloop';
import foundryAgentManifest from './foundryAgent';
import codingAgentManifest from './codingAgent';
import hostedAgentManifest from './hostedAgent';
import agentConnectorManifest from './agentLoopConnector';
import acaSessionManifest from './acaSessionConnector';
import a2aRequestManifest from './a2arequest';

// Workflow Operations
import invokeWorkflowManifest from './invokeWorkflow';

// Agent Handoff
import handoffManifest from './handoff';

// Type cast to handle manifest format differences
// The LogicAppsUX manifests use a slightly different format than our strict types
type ManifestLike = OperationManifest | { properties: Record<string, unknown> };

// =============================================================================
// MANIFEST REGISTRY
// =============================================================================

/**
 * Registry mapping operation type/kind to manifest.
 * Key format: "type" or "type/kind"
 */
const manifestRegistry: Record<string, ManifestLike> = {
  // Control Flow
  'If': conditionManifest,
  'Foreach': foreachManifest,
  'Switch': switchManifest,
  'Scope': scopeManifest,
  'Until': untilManifest,
  'Terminate': terminateManifest,

  // Variables
  'InitializeVariable': initializeManifest,
  'SetVariable': setManifest,
  'IncrementVariable': incrementManifest,
  'DecrementVariable': decrementManifest,
  'AppendToArrayVariable': appendArrayManifest,
  'AppendToStringVariable': appendStringManifest,

  // HTTP
  'Http': httpManifest,
  'Http/trigger': httpTriggerManifest,
  'HttpWithSwagger': httpWithSwaggerManifest,
  'HttpWithSwagger/trigger': httpWithSwaggerTriggerManifest,
  'HttpWebhook': httpWebhookManifest,
  'HttpWebhook/trigger': httpWebhookTriggerManifest,

  // Data Operations
  'Compose': composeManifest,
  'ParseJson': parseJsonManifest,
  'Table': csvTableManifest,
  'Table/csv': csvTableManifest,
  'Table/html': htmlTableManifest,
  'Join': joinManifest,
  'Select': selectManifest,
  'Query': queryManifest,

  // Date/Time
  'CurrentTime': currentTimeManifest,
  'AddToTime': addToTimeManifest,
  'SubtractFromTime': subtractFromTimeManifest,
  'GetFutureTime': getFutureTimeManifest,
  'GetPastTime': getPastTimeManifest,
  'ConvertTimeZone': convertTimezoneManifest,

  // Request/Response
  'Request': requestManifest,
  'Response': responseManifest,

  // Schedule
  'Recurrence': recurrenceManifest,
  // Note: SlidingWindow removed - consumption-only, not supported in Otto/Edge
  'Wait': delayManifest,
  'Wait/delay': delayManifest,
  'Wait/delayUntil': delayUntilManifest,
  'Delay': delayManifest,
  'DelayUntil': delayUntilManifest,

  // Inline Code Operations (multiple aliases for different casing conventions)
  'JavaScriptCode': inlineJavaScriptManifest,
  'javascriptCode': inlineJavaScriptManifest,
  'InlineJavaScript': inlineJavaScriptManifest,
  'ExecutePythonCode': inlinePythonManifest,
  'InlinePython': inlinePythonManifest,

  // Agent Operations
  'Agent': agentLoopManifest,
  'AgentLoop': agentLoopManifest,
  'FoundryAgent': foundryAgentManifest,
  'CodingAgent': codingAgentManifest,
  'HostedAgent': hostedAgentManifest,
  'AgentConnector': agentConnectorManifest,
  'AcaSession': acaSessionManifest,
  'A2aRequest': a2aRequestManifest,
  'Handoff': handoffManifest,
  'AgentHandoff': handoffManifest,

  // Workflow Operations
  'Workflow': invokeWorkflowManifest,
  'InvokeWorkflow': invokeWorkflowManifest,
};

// Create a case-insensitive lookup map
const manifestRegistryLower: Record<string, ManifestLike> = Object.fromEntries(
  Object.entries(manifestRegistry).map(([key, value]) => [key.toLowerCase(), value])
);

/**
 * Get manifest for a built-in operation by type and optional kind.
 * Lookup is case-insensitive.
 *
 * @param type - The operation type (e.g., "Http", "If", "Foreach")
 * @param kind - Optional kind for variations (e.g., "trigger" for Http triggers)
 * @returns The operation manifest, or null if not found
 */
export function getBuiltInManifest(type: string, kind?: string): OperationManifest | null {
  const typeLower = type.toLowerCase();
  const kindLower = kind?.toLowerCase();

  // Try type/kind combination first
  if (kindLower) {
    const manifest = manifestRegistryLower[`${typeLower}/${kindLower}`];
    if (manifest) return manifest as OperationManifest;
  }

  // Fall back to just type
  const manifest = manifestRegistryLower[typeLower];
  return manifest ? (manifest as OperationManifest) : null;
}

/**
 * Check if an operation type is a built-in operation with a manifest.
 * Lookup is case-insensitive.
 */
export function isBuiltInOperation(type: string, kind?: string): boolean {
  const typeLower = type.toLowerCase();
  const kindLower = kind?.toLowerCase();

  if (kindLower && manifestRegistryLower[`${typeLower}/${kindLower}`]) return true;
  return !!manifestRegistryLower[typeLower];
}

/**
 * Get all available built-in operation types.
 */
export function getBuiltInOperationTypes(): string[] {
  return Object.keys(manifestRegistry);
}

// =============================================================================
// EXPORTS
// =============================================================================

export {
  // Control Flow
  conditionManifest,
  foreachManifest,
  switchManifest,
  scopeManifest,
  untilManifest,
  terminateManifest,

  // Variables
  initializeManifest,
  setManifest,
  incrementManifest,
  decrementManifest,
  appendArrayManifest,
  appendStringManifest,

  // HTTP
  httpManifest,
  httpTriggerManifest,
  httpWithSwaggerManifest,
  httpWithSwaggerTriggerManifest,
  httpWebhookManifest,
  httpWebhookTriggerManifest,

  // Data Operations
  composeManifest,
  parseJsonManifest,
  csvTableManifest,
  htmlTableManifest,
  joinManifest,
  selectManifest,
  queryManifest,

  // Date/Time
  currentTimeManifest,
  addToTimeManifest,
  subtractFromTimeManifest,
  getFutureTimeManifest,
  getPastTimeManifest,
  convertTimezoneManifest,

  // Request/Response
  requestManifest,
  responseManifest,

  // Schedule
  recurrenceManifest,
  // Note: SlidingWindow removed - consumption-only, not supported in Otto/Edge
  delayManifest,
  delayUntilManifest,

  // Inline Code Operations
  inlineJavaScriptManifest,
  inlinePythonManifest,

  // Agent Operations
  agentLoopManifest,
  foundryAgentManifest,
  codingAgentManifest,
  hostedAgentManifest,
  agentConnectorManifest,
  acaSessionManifest,
  a2aRequestManifest,
  handoffManifest,

  // Workflow Operations
  invokeWorkflowManifest,
};
